import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">
                        Data Type Manager Microservices
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700 leading-relaxed">

                    <h2 className="text-lg font-semibold text-black">Data Type Manager</h2>

                    {/* CREATE DATA TYPE */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Creating a Data Type</h3>
                        <div>
                            <p>Users can create a data type by providing:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Data Type Name</li>
                                <li>Data Type Label</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold text-black">Ways to Create a Data Type</p>
                            <p className="mt-1">Data types can be created using the following methods:</p>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Manual Creation</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Navigate to the Data Type Manager.</li>
                                <li>Click the <span className="font-medium">Add Data Type</span> button.</li>
                                <li>Enter the required details and save.</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">AI / NLPS Console</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Data types can also be created using natural language commands through the AI/NLPS Console.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-1">
                            <p className="font-semibold mb-1">Example Commands</p>
                            <p>Create a data type named XYZ</p>
                            <p>Create data types named XYZ, ABC, PQR</p>
                        </div>
                    </section>

                    {/* CREATE ATTRIBUTES */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Creating Attributes Type</h3>
                        <p>
                            Users can create attributes by clicking the Plus (+) icon in the Action column of the
                            Data Type List. Attributes can be added for a specific data type using this Plus icon.
                        </p>

                        <div>
                            <p className="font-semibold text-black">Ways to Create Attributes</p>
                            <p className="mt-1">Attributes can be created using the following methods:</p>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Manual Creation</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Navigate to the Data Type Manager.</li>
                                <li>Click the Plus icon.</li>
                                <li>Enter the required details.</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Add Attributes Form</p>
                            <ol className="list-decimal ml-5 mt-1 space-y-1">
                                <li>
                                    <span className="font-medium">Select Type</span> – Attributes can be of any
                                    type, such as string, integer, decimal, object, etc.
                                </li>
                                <li>
                                    <span className="font-medium">Select CM Values</span> – Code Manager values
                                    can be selected here. The selected Code Manager values will be displayed in
                                    the attribute field dropdown in the form.
                                </li>
                                <li>
                                    <span className="font-medium">Select Source Datatype and Source Attributes</span> –
                                    Source data types and their attributes can be selected from the dropdown.
                                </li>
                            </ol>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">AI / NLPS Console</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Attributes can also be created using natural language commands through the AI/NLPS Console.</li>
                            </ul>
                        </div>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-1">
                            <p className="font-semibold mb-1">Example Commands</p>
                            <p>Add attribute d in datatype gef</p>
                            <p>Add attributes a,b,c in datatype gef</p>
                        </div>
                    </section>

                    {/* DATA QUALITY */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Data Quality</h3>
                        <p>The Data Quality module identifies and logs the following three types of issues:</p>
                        <ul className="space-y-2 ml-5">
                            <li>
                                <span className="font-semibold">Duplicate</span> – Identifies data types with the same name.
                            </li>
                            <li>
                                <span className="font-semibold">Orphans</span> – Identifies attributes that do not have a valid parent data type reference.
                            </li>
                            <li>
                                <span className="font-semibold">Overlaps</span> – Identifies attributes with the same name that exist under different data types.
                            </li>
                        </ul>
                    </section>

                    {/* IMPORT / EXPORT */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Import / Export</h3>

                        <div>
                            <p className="font-semibold">Import</p>
                            <p className="mt-1">Data Types and Attributes can be added by importing files.</p>
                            <p className="mt-1 font-medium">Supported file formats:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>JSON</li>
                                <li>CSV</li>
                                <li>Word</li>
                                <li>PDF</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-semibold mt-2">Export</p>
                            <p className="mt-1">Data can be exported in different formats and scopes.</p>
                            <p className="mt-1 font-medium">Available export scopes:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>Full Dataset</li>
                                <li>Data Types Only</li>
                                <li>Attributes Only</li>
                            </ul>
                        </div>
                    </section>

                    {/* AUDIT LOG */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">Audit Log</h3>
                        <p>The Audit Log records all system errors related to the Data Type Manager, including:</p>
                        <ul className="list-disc ml-5 space-y-0.5">
                            <li>Validation errors</li>
                            <li>Authentication errors</li>
                            <li>Database errors</li>
                        </ul>
                        <div>
                            <p className="font-medium">Each log entry includes:</p>
                            <ul className="list-disc ml-5 mt-1 space-y-0.5">
                                <li>IP address</li>
                                <li>Status code</li>
                                <li>Error timestamp</li>
                                <li>Error type</li>
                            </ul>
                        </div>
                    </section>

                    {/* AI/NLPS CONSOLE */}
                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">AI/NLPS Console</h3>
                        <p>
                            The AI Console allows users to add, edit, retrieve, and delete data types and
                            attributes. The system supports adding multiple items in a single operation.
                        </p>

                        <div className="bg-gray-100 p-3 rounded text-xs space-y-1 max-h-64 overflow-y-auto">
                            <p className="font-semibold mb-2">Example Commands</p>
                            <p>Create datatype whose name is abc</p>
                            <p>Create datatype whose name is abc and label is mop</p>
                            <p>Create datatype abc with attributes def,jhi,klm. Create another datatype sop</p>
                            <p>Create datatype abc with attributes def,jhi,klm. Create another datatype sop with label kos.</p>
                            <p>Create datatype abc with label ssd and attributes def,jhi,klm and mnt</p>
                            <p>Create datatype abc with label ssd and attributes def,jhi,klm and mnt. Create another datatype ppl, mml, xxm and jjc.</p>
                            <p>Create datatype gef and copy all attributes of abc</p>
                            <p>Copy all attributes of a to b</p>
                            <p>Update attribute a to k in datatype gef</p>
                            <p>Update label of a to bmn in gef</p>
                            <p>Delete attribute a in gef</p>
                            <p>Delete datatype abc</p>
                            <p>Delete attributes a, b and c in gef</p>
                            <p>Delete datatypes abc and gef</p>
                            <p>Delete all attributes in datatype abc</p>
                        </div>
                    </section>

                </div>
            </DialogContent>
        </Dialog>
    );
}